import {useEffect, useRef, useState} from "react";
import {toSelectedFile} from "core/transfer/selection";
import {captureDroppedFiles, DroppedCapture} from "./dropSelection";

/** True when a drag payload carries files (vs. selected text, links or page elements). */
function dragCarriesFiles(dataTransfer: DataTransfer | null): boolean {
	if (!dataTransfer) return false;
	// `types` is a DOMStringList in browsers and a plain array in tests — normalise then test.
	return Array.from(dataTransfer.types ?? []).includes("Files");
}

interface Options {
	/** Whole-page dragging is only live in the create phase (design: `screen==='create'`). */
	enabled: boolean;
	/**
	 * Called once per completed drop that carried anything (already gated to file drags): raw `File`s
	 * for plain drops (synchronously, as always), path-aware entries once a dropped FOLDER finished
	 * its async traversal (ui#94 — see {@link captureDroppedFiles}).
	 *
	 * ONE callback rather than a files callback plus a losses callback: the sender gets a single
	 * toast, so a drop that both added files and lost an empty folder has to reach the caller as one
	 * event — two would race and the second would erase the first (ui#1308).
	 */
	onDrop: (capture: DroppedCapture) => void;
}

/**
 * Window-wide file drag-and-drop state, mirroring the design's `componentDidMount`
 * handlers (`Transferify.dc.html`): binds dragenter/dragover/dragleave/drop on `window`
 * and reports whether a file drag is currently hovering the viewport.
 *
 * SSG-safe: `window` is only touched inside the effect (never at module scope or during
 * render), and the listeners are torn down on unmount / when `enabled` flips to false.
 *
 * Robustness: a depth counter (bumped on every dragenter, dropped on every dragleave)
 * survives the enter/leave storm as the pointer crosses nested children, so the overlay
 * only clears once the drag has truly left the window (`depth <= 0`). Non-file drags never
 * raise the overlay, and a completed drop resets the counter unconditionally.
 */
export function useWindowFileDrag({enabled, onDrop}: Options): boolean {
	const [isDragActive, setIsDragActive] = useState(false);
	// Keep the newest onDrop reachable without re-binding listeners on every render.
	const onDropRef = useRef(onDrop);
	onDropRef.current = onDrop;

	useEffect(() => {
		if (!enabled) return;
		let depth = 0;

		const onOver = (e: DragEvent) => {
			// preventDefault on dragover is what lets the browser fire `drop` (instead of
			// navigating to the file) — required even before the overlay is shown.
			e.preventDefault();
		};
		const onEnter = (e: DragEvent) => {
			e.preventDefault();
			depth += 1;
			if (dragCarriesFiles(e.dataTransfer)) setIsDragActive(true);
		};
		const onLeave = () => {
			depth -= 1;
			if (depth <= 0) {
				depth = 0;
				setIsDragActive(false);
			}
		};
		const deliver = (capture: DroppedCapture) => {
			// An empty-folder-only drop still reports: that case is exactly the one that used to look
			// like the app ignoring the gesture entirely (ui#1308).
			if (capture.files.length || capture.foldersWithoutFiles.length) onDropRef.current(capture);
		};
		const onWindowDrop = (e: DragEvent) => {
			e.preventDefault();
			depth = 0;
			setIsDragActive(false);
			if (!e.dataTransfer) return;
			// Capture is SYNCHRONOUS (DataTransferItems die with the event); only a drop that
			// contains a folder resolves later, after its Entries-API traversal (ui#94).
			const captured = captureDroppedFiles(e.dataTransfer);
			if (Array.isArray(captured)) {
				deliver({files: captured.map(toSelectedFile), foldersWithoutFiles: []});
				return;
			}
			void captured.then(deliver);
		};

		window.addEventListener("dragover", onOver);
		window.addEventListener("dragenter", onEnter);
		window.addEventListener("dragleave", onLeave);
		window.addEventListener("drop", onWindowDrop);
		return () => {
			window.removeEventListener("dragover", onOver);
			window.removeEventListener("dragenter", onEnter);
			window.removeEventListener("dragleave", onLeave);
			window.removeEventListener("drop", onWindowDrop);
			setIsDragActive(false);
		};
	}, [enabled]);

	return isDragActive;
}
